/**
 * Theme Context — Light/Dark theme toggle with localStorage persistence
 */
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { createGeoveraTheme, palettes, type ThemeMode } from './index';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  palette: typeof palettes.dark;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'geovera-theme-mode';

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Ignore localStorage errors
  }
  // Default to dark for GIS professional feel
  return 'dark';
}

export function GeoveraThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const theme = useMemo(() => createGeoveraTheme(mode), [mode]);
  const palette = useMemo(() => palettes[mode], [mode]);

  // Update document background to prevent flash
  useEffect(() => {
    document.documentElement.style.backgroundColor = palette.bg.base;
    document.documentElement.style.colorScheme = mode;
  }, [mode, palette]);

  const value = useMemo(
    () => ({ mode, toggleMode, setMode, palette }),
    [mode, toggleMode, setMode, palette]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useGeoveraTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useGeoveraTheme must be used within GeoveraThemeProvider');
  }
  return context;
}
